import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { X } from "lucide-react";
import {
  createAutomation,
  deleteAutomation,
  listAutomations,
  listAutomationRuns,
  updateAutomation,
} from "../api/boardApi";
import { errorToast, successToast } from "../../../redux/actionCreator";

// Recipe-builder UI for board automations. Sentence-style:
//   "When [trigger]   →   [action 1], [action 2], ..."
//
// Trigger and action shapes are described in
// smarthatch-boards-service/src/lib/board/automationRunner.ts. Keep them in
// sync when extending.
export default function AutomationsModal({ board, onClose }) {
  const [tab, setTab] = useState("list"); // "list" | "new" | "runs"
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        listAutomations(board._id),
        listAutomationRuns(board._id),
      ]);
      setItems(a.data || []);
      setRuns(r.data || []);
    } catch (e) {
      errorToast(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [board._id]);

  const handleToggle = async (a) => {
    try {
      await updateAutomation(board._id, a._id, { enabled: !a.enabled });
      setItems((arr) => arr.map((x) => (x._id === a._id ? { ...x, enabled: !x.enabled } : x)));
    } catch (e) {
      errorToast(e.message);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete automation "${a.name}"?`)) return;
    try {
      await deleteAutomation(board._id, a._id);
      setItems((arr) => arr.filter((x) => x._id !== a._id));
      successToast("Deleted");
    } catch (e) {
      errorToast(e.message);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg" dialogClassName="automation-modal">
      <Modal.Header closeButton>
        <Modal.Title>Automations — {board.name}</Modal.Title>
      </Modal.Header>
      <div className="automation-tabs">
        <button className={`tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>Recipes ({items.length})</button>
        <button className={`tab ${tab === "new" ? "active" : ""}`} onClick={() => { setEditing(null); setTab("new"); }}>+ New</button>
        <button className={`tab ${tab === "runs" ? "active" : ""}`} onClick={() => setTab("runs")}>Run log ({runs.length})</button>
      </div>

      <Modal.Body>
        {loading && <div className="muted"><Spinner size="sm" animation="border" /> Loading…</div>}

        {!loading && tab === "list" && (
          <>
            {items.length === 0 && (
              <div className="empty-state">
                No automations yet. <button className="linkish" onClick={() => setTab("new")}>Create your first recipe →</button>
              </div>
            )}
            {items.map((a) => (
              <div key={a._id} className={`automation-row ${a.enabled ? "" : "off"}`}>
                <div className="automation-row-head">
                  <Form.Check
                    type="switch"
                    checked={a.enabled}
                    onChange={() => handleToggle(a)}
                    label={<strong>{a.name}</strong>}
                  />
                  <div className="row-actions">
                    <button
                      className="linkish"
                      onClick={() => { setEditing(a); setTab("new"); }}
                    >Edit</button>
                    <button className="linkish del" onClick={() => handleDelete(a)}>Delete</button>
                  </div>
                </div>
                <div className="automation-recipe">
                  <RecipeSentence board={board} trigger={a.trigger} actions={a.actions} />
                </div>
                {a.last_error && (
                  <div className="automation-error">Last error: {a.last_error}</div>
                )}
                {a.last_run_at && !a.last_error && (
                  <div className="muted small">Last ran {new Date(a.last_run_at).toLocaleString()}</div>
                )}
              </div>
            ))}
          </>
        )}

        {!loading && tab === "new" && (
          <RecipeEditor
            board={board}
            existing={editing}
            onSave={async (body) => {
              try {
                if (editing) {
                  const res = await updateAutomation(board._id, editing._id, body);
                  setItems((arr) => arr.map((x) => (x._id === editing._id ? res.data : x)));
                  successToast("Updated");
                } else {
                  const res = await createAutomation(board._id, body);
                  setItems((arr) => [res.data, ...arr]);
                  successToast("Created");
                }
                setTab("list");
                setEditing(null);
              } catch (e) {
                errorToast(e.message);
              }
            }}
            onCancel={() => { setTab("list"); setEditing(null); }}
          />
        )}

        {!loading && tab === "runs" && (
          <>
            {runs.length === 0 && <div className="muted">No runs yet.</div>}
            {runs.map((r) => (
              <div key={r._id} className={`run-row ${r.status}`}>
                <span className={`run-badge ${r.status}`}>{r.status}</span>
                <span className="run-trigger">{r.trigger_kind}</span>
                <span className="muted small">{new Date(r.createdAt).toLocaleString()}</span>
                <div className="run-summary">{r.summary || r.error || ""}</div>
              </div>
            ))}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Sentence renderer (human-readable summary on the list view) ──────
function RecipeSentence({ board, trigger, actions }) {
  const colTitle = (id) => board.columns.find((c) => c._id === id)?.title || "(unknown)";
  const groupTitle = (id) => board.groups.find((g) => g._id === id)?.title || "(any group)";
  const labelText = (colId, lblId) => {
    const c = board.columns.find((x) => x._id === colId);
    return c?.settings?.labels?.find((l) => l.id === lblId)?.label || lblId;
  };

  let triggerStr = "";
  if (trigger.kind === "status_changes_to") {
    triggerStr = `${colTitle(trigger.column_id)} changes to "${labelText(trigger.column_id, trigger.label_id)}"`;
  } else if (trigger.kind === "item_created") {
    triggerStr = `an item is created${trigger.group_id ? ` in "${groupTitle(trigger.group_id)}"` : ""}`;
  } else if (trigger.kind === "item_moved_to_group") {
    triggerStr = `an item is moved to "${groupTitle(trigger.group_id)}"`;
  }

  const actionStrs = (actions || []).map((a) => {
    if (a.kind === "set_cell") return `set ${colTitle(a.column_id)} to "${a.value}"`;
    if (a.kind === "move_to_group") return `move to "${groupTitle(a.group_id)}"`;
    if (a.kind === "notify_person") return `notify the ${colTitle(a.column_id)}`;
    return a.kind;
  });

  return (
    <>
      <span className="recipe-when">When</span> {triggerStr}, <span className="recipe-then">then</span>{" "}
      {actionStrs.join(", ")}.
    </>
  );
}

// ── Recipe editor form ────────────────────────────────────────────────
function RecipeEditor({ board, existing, onSave, onCancel }) {
  const statusCols = (board.columns || []).filter((c) => c.type === "status");
  const peopleCols = (board.columns || []).filter((c) => c.type === "people");
  const settableCols = (board.columns || []).filter((c) =>
    ["status", "text", "long_text", "number", "checkbox", "dropdown"].includes(c.type),
  );
  const groups = board.groups || [];

  const [name, setName] = useState(existing?.name || "");
  const [trigger, setTrigger] = useState(existing?.trigger || { kind: "status_changes_to", column_id: statusCols[0]?._id || null, label_id: null });
  const [actions, setActions] = useState(existing?.actions || [{ kind: "set_cell", column_id: statusCols[0]?._id || null, value: "" }]);

  const updateTrigger = (patch) => setTrigger((t) => ({ ...t, ...patch }));
  const updateAction = (idx, patch) => setActions((arr) => arr.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  const addAction = () => setActions((arr) => [...arr, { kind: "set_cell", column_id: settableCols[0]?._id || null, value: "" }]);
  const removeAction = (idx) => setActions((arr) => arr.filter((_, i) => i !== idx));

  const handleTriggerKind = (k) => {
    if (k === "status_changes_to") setTrigger({ kind: k, column_id: statusCols[0]?._id || null, label_id: null });
    else if (k === "item_created") setTrigger({ kind: k, group_id: null });
    else if (k === "item_moved_to_group") setTrigger({ kind: k, group_id: groups[0]?._id || null });
  };

  const handleActionKind = (idx, k) => {
    if (k === "set_cell") updateAction(idx, { kind: k, column_id: settableCols[0]?._id || null, value: "", group_id: null });
    else if (k === "move_to_group") updateAction(idx, { kind: k, group_id: groups[0]?._id || null, column_id: null, value: null });
    else if (k === "notify_person") updateAction(idx, { kind: k, column_id: peopleCols[0]?._id || null, value: null, group_id: null });
  };

  const triggerColumn = trigger.column_id ? board.columns.find((c) => c._id === trigger.column_id) : null;
  const triggerLabels = triggerColumn?.settings?.labels || [];

  const canSave = () => {
    if (!name.trim()) return false;
    if (trigger.kind === "status_changes_to" && (!trigger.column_id || !trigger.label_id)) return false;
    if (trigger.kind === "item_moved_to_group" && !trigger.group_id) return false;
    if (actions.length === 0) return false;
    for (const a of actions) {
      if (a.kind === "set_cell" && !a.column_id) return false;
      if (a.kind === "move_to_group" && !a.group_id) return false;
      if (a.kind === "notify_person" && !a.column_id) return false;
    }
    return true;
  };

  const submit = () => {
    onSave({ name: name.trim(), trigger, actions, enabled: existing?.enabled ?? true });
  };

  return (
    <div className="recipe-editor">
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Notify owner when status changes to Done"
        />
      </Form.Group>

      <div className="builder-card">
        <div className="builder-when">WHEN</div>
        <div className="builder-row">
          <Form.Select className="builder-control" value={trigger.kind} onChange={(e) => handleTriggerKind(e.target.value)}>
            <option value="status_changes_to">A status column changes to…</option>
            <option value="item_created">An item is created</option>
            <option value="item_moved_to_group">An item is moved to a group</option>
          </Form.Select>

          {trigger.kind === "status_changes_to" && (
            <>
              <Form.Select className="builder-control" value={trigger.column_id || ""} onChange={(e) => updateTrigger({ column_id: e.target.value, label_id: null })}>
                {statusCols.map((c) => <option key={c._id} value={c._id}>{c.title}</option>)}
              </Form.Select>
              <span className="builder-glue">→</span>
              <Form.Select className="builder-control" value={trigger.label_id || ""} onChange={(e) => updateTrigger({ label_id: e.target.value })}>
                <option value="">— pick a label —</option>
                {triggerLabels.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </Form.Select>
            </>
          )}
          {trigger.kind === "item_created" && (
            <Form.Select className="builder-control" value={trigger.group_id || ""} onChange={(e) => updateTrigger({ group_id: e.target.value || null })}>
              <option value="">in any group</option>
              {groups.map((g) => <option key={g._id} value={g._id}>{g.title}</option>)}
            </Form.Select>
          )}
          {trigger.kind === "item_moved_to_group" && (
            <Form.Select className="builder-control" value={trigger.group_id || ""} onChange={(e) => updateTrigger({ group_id: e.target.value })}>
              {groups.map((g) => <option key={g._id} value={g._id}>{g.title}</option>)}
            </Form.Select>
          )}
        </div>
      </div>

      <div className="builder-card">
        <div className="builder-when">THEN</div>
        {actions.map((a, i) => {
          const setCol = a.kind === "set_cell" && a.column_id ? board.columns.find((c) => c._id === a.column_id) : null;
          const setLabels = setCol?.settings?.labels || [];
          return (
            <div key={i} className="builder-row">
              <Form.Select className="builder-control" value={a.kind} onChange={(e) => handleActionKind(i, e.target.value)}>
                <option value="set_cell">Set a cell value</option>
                <option value="move_to_group">Move to a group</option>
                <option value="notify_person">Notify a person</option>
              </Form.Select>

              {a.kind === "set_cell" && (
                <>
                  <Form.Select className="builder-control" value={a.column_id || ""} onChange={(e) => updateAction(i, { column_id: e.target.value, value: "" })}>
                    {settableCols.map((c) => <option key={c._id} value={c._id}>{c.title}</option>)}
                  </Form.Select>
                  <span className="builder-glue">→</span>
                  {setCol?.type === "status" ? (
                    <Form.Select className="builder-control" value={a.value || ""} onChange={(e) => updateAction(i, { value: e.target.value })}>
                      <option value="">— pick a label —</option>
                      {setLabels.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </Form.Select>
                  ) : setCol?.type === "checkbox" ? (
                    <Form.Select className="builder-control" value={String(a.value)} onChange={(e) => updateAction(i, { value: e.target.value === "true" })}>
                      <option value="true">checked</option>
                      <option value="false">unchecked</option>
                    </Form.Select>
                  ) : (
                    <Form.Control className="builder-control" placeholder="value" value={a.value || ""} onChange={(e) => updateAction(i, { value: e.target.value })} />
                  )}
                </>
              )}
              {a.kind === "move_to_group" && (
                <Form.Select className="builder-control" value={a.group_id || ""} onChange={(e) => updateAction(i, { group_id: e.target.value })}>
                  {groups.map((g) => <option key={g._id} value={g._id}>{g.title}</option>)}
                </Form.Select>
              )}
              {a.kind === "notify_person" && (
                <Form.Select className="builder-control" value={a.column_id || ""} onChange={(e) => updateAction(i, { column_id: e.target.value })}>
                  <option value="">— pick a people column —</option>
                  {peopleCols.map((c) => <option key={c._id} value={c._id}>{c.title}</option>)}
                </Form.Select>
              )}
              {actions.length > 1 && (
                <button className="linkish del" onClick={() => removeAction(i)} aria-label="Remove action"><X size={12} /></button>
              )}
            </div>
          );
        })}
        <Button variant="link" size="sm" onClick={addAction}>+ Add action</Button>
      </div>

      <div className="builder-footer">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={!canSave()}>
          {existing ? "Save changes" : "Create automation"}
        </Button>
      </div>
    </div>
  );
}
